import React, { useCallback } from 'react';
import { Button, Tooltip, Typography } from 'antd';
import { RocketOutlined } from '@ant-design/icons';
import { useAuthStore } from '../../stores/authStore';
import { shouldLoadCanvasQuota } from '../editor/CanvasQuota';
import { useMyQuota } from '../../hooks/useMyQuota';
import { isQuotaNearLimit, openNewVersion, resolveNewVersionUrl } from '../../utils/newVersionEntry';
import './NewVersionEntry.css';

const { Text } = Typography;

export interface NewVersionEntryProps {
  /** 视觉强调程度，画布页用 prominent。 */
  emphasis?: 'default' | 'prominent';
  /** 额度接近上限时在按钮旁显示小字提示。 */
  showQuotaHint?: boolean;
  /** 覆盖跳转地址，主要用于测试。 */
  url?: string;
  className?: string;
}

/**
 * 新版入口按钮：点击在新标签页打开新版站点。
 *
 * 用于引导用户尝试新版，因此在视觉上做渐变高亮；当今日额度使用率达到
 * 90% 时，额外提示新版有独立额度，进一步促进点击。
 */
const NewVersionEntry: React.FC<NewVersionEntryProps> = ({
  emphasis = 'default',
  showQuotaHint = false,
  url,
  className,
}) => {
  const role = useAuthStore((state) => state.user?.role);
  const quota = useMyQuota(showQuotaHint && shouldLoadCanvasQuota(role));

  const targetUrl = resolveNewVersionUrl(url);
  const nearLimit = Boolean(
    quota && isQuotaNearLimit(quota.used, quota.limit),
  );

  const handleClick = useCallback(() => {
    openNewVersion(targetUrl);
  }, [targetUrl]);

  const button = (
    <Button
      type="primary"
      size="small"
      className={`new-version-entry new-version-entry--${emphasis}${className ? ` ${className}` : ''}`}
      icon={<RocketOutlined />}
      onClick={handleClick}
    >
      体验新版
    </Button>
  );

  return (
    <span className="new-version-entry__wrapper" data-testid="new-version-entry">
      <Tooltip title="前往新版创意中心（新标签页打开）">
        {button}
      </Tooltip>
      {showQuotaHint && nearLimit && (
        <Text className="new-version-entry__hint">使用新版独立额度</Text>
      )}
    </span>
  );
};

export default NewVersionEntry;